import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Microservice } from '@core-interfaces/global/microservice';
import type { ApiSuccessResponse } from '@core-interfaces/global/http';
import type { IMyPermissions } from '@core-interfaces/global/permissions';

/**
 * Cachea los permisos efectivos del administrador autenticado.
 * Los permisos se resuelven en el backend (rol del back office) y se obtienen
 * una sola vez por sesión vía GET /backoffice/users/me/permissions.
 *
 * Fail-closed: ante cualquier error la caché queda sin permisos, de modo que
 * las funcionalidades protegidas permanecen ocultas/bloqueadas.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private permissions = new Set<string>();
  private superFlag = false;
  private loaded = false;
  private inFlight$?: Observable<boolean>;

  /** Emite cada vez que cambia el set de permisos (carga inicial o limpieza). */
  private readonly changesSubject = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient) {}

  /** Carga los permisos una sola vez. Llamadas concurrentes comparten la misma petición. */
  ensureLoaded(): Observable<boolean> {
    if (this.loaded) {
      return of(true);
    }
    if (this.inFlight$) {
      return this.inFlight$;
    }
    this.inFlight$ = this.http
      .get<ApiSuccessResponse<IMyPermissions>>(
        `/${Microservice.backoffice_service}/users/me/permissions`,
      )
      .pipe(
        map((res) => {
          this.setData(res.data.permissions ?? [], res.data.is_super ?? false);
          return true;
        }),
        catchError(() => {
          // Fail-closed: sin permisos efectivos.
          this.setData([], false);
          return of(true);
        }),
        shareReplay(1),
      );
    return this.inFlight$;
  }

  /** Fuerza una recarga (p. ej. tras iniciar sesión con otra cuenta). */
  reload(): Observable<boolean> {
    this.clear();
    return this.ensureLoaded();
  }

  /** Comprobación síncrona contra la caché. El Super Admin pasa siempre. */
  hasPermission(permission: string): boolean {
    if (this.superFlag) {
      return true;
    }
    return this.permissions.has(permission);
  }

  /** True si posee al menos uno de los permisos indicados. */
  hasAny(permissions: string[]): boolean {
    if (this.superFlag) {
      return true;
    }
    return permissions.some((permission) => this.permissions.has(permission));
  }

  /** True si posee todos los permisos indicados. */
  hasAll(permissions: string[]): boolean {
    if (this.superFlag) {
      return true;
    }
    return permissions.every((permission) => this.permissions.has(permission));
  }

  isSuper(): boolean {
    return this.superFlag;
  }

  /** Observable que notifica cargas/limpiezas para refrescar la UI reactivamente. */
  changes$(): Observable<boolean> {
    return this.changesSubject.asObservable();
  }

  /** Limpia la caché (logout). */
  clear(): void {
    this.permissions.clear();
    this.superFlag = false;
    this.loaded = false;
    this.inFlight$ = undefined;
    this.changesSubject.next(false);
  }

  private setData(permissions: string[], isSuper: boolean): void {
    this.permissions = new Set(permissions);
    this.superFlag = isSuper;
    this.loaded = true;
    this.inFlight$ = undefined;
    this.changesSubject.next(true);
  }
}
